import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";

const NUM_FIELDS = [
  ["follow_ups_completed",   "Follow-ups completed"],
  ["meetings_scheduled",     "Meetings scheduled"],
  ["rfqs_identified",        "RFQs identified"],
  ["rfqs_received",          "RFQs received"],
  ["quotations_prepared",    "Quotations prepared"],
  ["quotations_submitted",   "Quotations submitted"],
  ["purchase_orders_received", "Purchase orders received"],
  ["orders_won",             "Orders won"],
];

export default function DailyStandup() {
  const [today] = useState(new Date().toISOString().slice(0, 10));
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(
    Object.fromEntries(NUM_FIELDS.map(([k]) => [k, 0]).concat([["revenue_generated_usd", 0], ["blockers_text", ""], ["self_note", ""]]))
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/standup/today").then((r) => {
      if (r.data) {
        setExisting(r.data);
        setForm({ ...form, ...r.data });
      }
    });
    // eslint-disable-next-line
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { date: today, ...form };
      const { data } = await http.post("/standup", payload);
      setExisting(data);
      toast.success("Standup submitted and locked for today");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Daily Standup" subtitle={`Log your day: ${today}`}>
      <div className="max-w-3xl">
        {existing?.locked && (
          <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center gap-2 mb-6" data-testid="standup-locked-banner">
            <Lock className="w-4 h-4" />
            Today's standup was submitted at {new Date(existing.submitted_at).toLocaleTimeString()}. Locked to prevent retroactive edits — supervisor override needed.
          </div>
        )}

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-6" data-testid="standup-form">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Numbers only the system can't see</h2>
            <p className="text-xs text-slate-500 mt-1">Everything else (leads added, verifications, industries) is auto-computed. Target: under 60 seconds.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {NUM_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">{label}</Label>
                <Input
                  type="number"
                  min={0}
                  value={form[k]}
                  onChange={(e) => update(k, parseInt(e.target.value, 10) || 0)}
                  disabled={existing?.locked}
                  data-testid={`standup-${k}`}
                  className="mt-1.5 font-mono text-lg font-semibold text-center"
                />
              </div>
            ))}
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Revenue generated (USD)</Label>
            <Input type="number" min={0} step="0.01"
                   value={form.revenue_generated_usd}
                   onChange={(e) => update("revenue_generated_usd", parseFloat(e.target.value) || 0)}
                   disabled={existing?.locked}
                   data-testid="standup-revenue"
                   className="mt-1.5 font-mono text-lg font-semibold" />
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Blockers</Label>
            <Textarea value={form.blockers_text || ""}
                      onChange={(e) => update("blockers_text", e.target.value)}
                      disabled={existing?.locked}
                      data-testid="standup-blockers"
                      rows={2}
                      className="mt-1.5" placeholder="What's slowing you down?" />
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Self note</Label>
            <Textarea value={form.self_note || ""}
                      onChange={(e) => update("self_note", e.target.value)}
                      disabled={existing?.locked}
                      data-testid="standup-selfnote"
                      rows={2}
                      className="mt-1.5" placeholder="Wins, learnings, priorities for tomorrow" />
          </div>

          <Button type="submit" disabled={busy || existing?.locked}
                  data-testid="standup-submit"
                  className="bg-amber-600 hover:bg-amber-700 text-white h-11 w-full sm:w-auto">
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {existing?.locked ? "Already submitted for today" : (busy ? "Submitting…" : "Submit standup")}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
